import { capitalCase } from 'change-case';
// @mui
import { Container, Tab, Tabs, Card } from '@mui/material';
import { styled } from '@mui/material/styles';
// routes
// hooks
import useTabs from '../../hooks/useTabs';
import useSettings from '../../hooks/useSettings';
// components
import Page from '../../components/Page';
import Iconify from '../../components/Iconify';
import { useLocation } from 'react-router-dom';
import { Outlet, useNavigate, useParams } from 'react-router';
import { Member, useGetMemberQuery } from 'src/graphql/generated';
import LoadingScreen from 'src/components/LoadingScreen';
import MemberCover from './MemberCover';

// ----------------------------------------------------------------------

const TabsWrapperStyle = styled('div')(({ theme }) => ({
  zIndex: 9,
  bottom: 0,
  width: '100%',
  display: 'flex',
  position: 'absolute',
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'center',
  },
  [theme.breakpoints.up('md')]: {
    justifyContent: 'flex-end',
    paddingRight: theme.spacing(3),
  },
}));

export type MemberLayoutContext = {
  member: Member;
};

export default function MemberLayout() {
  const { themeStretch } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();

  const { id } = useParams();

  const { data, loading } = useGetMemberQuery({
    variables: {
      id: id || '',
    },
  });

  const ACCOUNT_TABS = [
    {
      value: 'home',
      icon: <Iconify icon={'ic:baseline-dashboard'} width={20} height={20} />,
      path: 'home',
    },
    {
      value: 'detail',
      icon: <Iconify icon={'ic:round-account-box'} width={20} height={20} />,
      path: 'detail',
    },
    {
      value: 'membership',
      icon: <Iconify icon={'ic:baseline-card-membership'} width={20} height={20} />,
      path: 'membership',
    },
    {
      value: 'history',
      icon: <Iconify icon={'ant-design:history-outlined'} width={20} height={20} />,
      path: 'membership',
    },
    {
      value: 'billing',
      icon: <Iconify icon={'ant-design:dollar-circle-outlined'} width={20} height={20} />,
      path: 'billing',
    },
    {
      value: 'accessControl',
      icon: <Iconify icon={'academicons:open-access'} width={20} height={20} />,
      path: 'accessControl',
    },
  ];

  const defaultTab = () => {
    const matchingTab = ACCOUNT_TABS.find((tab) => location.pathname.includes(tab.path));
    return matchingTab?.value || 'profile';
  };

  const { currentTab, onChangeTab } = useTabs(defaultTab());

  if (loading) {
    return <LoadingScreen />;
  }

  const member: Member | undefined = data?.getMember as Member;

  return (
    <Page title={`Member: ${member?.displayName}`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <Card
          sx={{
            mb: 3,
            height: { xs: 288, md: 180 },
            position: 'relative',
          }}
        >
          <MemberCover member={member} />

          <TabsWrapperStyle>
            <Tabs
              allowScrollButtonsMobile
              variant="scrollable"
              scrollButtons="auto"
              value={currentTab}
              onChange={onChangeTab}
            >
              {ACCOUNT_TABS.map((tab) => (
                <Tab
                  disableRipple
                  key={tab.value}
                  label={capitalCase(tab.value)}
                  icon={tab.icon}
                  value={tab.value}
                  onClick={() => navigate(tab.path)}
                />
              ))}
            </Tabs>
          </TabsWrapperStyle>
        </Card>

        <Outlet context={{ member }} />
      </Container>
    </Page>
  );
}
